/*
  Maze Oasis scroll reveal
  Vanilla JS, IntersectionObserver only. See the usage notes in mo-base.css.
  - [data-mo-reveal]   element fades / slides in once when it enters the viewport
  - [data-mo-stagger]  direct children reveal in sequence (stagger from theme settings)
  - [data-mo-progress] receives --mo-progress (0 to 1) while it scrolls through the viewport,
                       for pinned / sticky storytelling sections
  Disabled entirely when html.mo-anim is missing (global toggle off or reduced motion).
*/
(function () {
  'use strict';

  var root = document.documentElement;
  var REVEALED = 'mo-revealed';
  var DISABLED = '[data-mo-animate="false"]';
  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function enabled() {
    return root.classList.contains('mo-anim') && !reducedMotion.matches;
  }

  function staggerStep() {
    var value = parseFloat(getComputedStyle(root).getPropertyValue('--mo-reveal-stagger'));
    return isNaN(value) ? 80 : value;
  }

  function isDisabled(el) {
    return !!el.closest(DISABLED);
  }

  function revealAll(scope) {
    var nodes = (scope || document).querySelectorAll('[data-mo-reveal], [data-mo-stagger] > *');
    for (var i = 0; i < nodes.length; i++) nodes[i].classList.add(REVEALED);
  }

  /* ---------- Reveal ---------- */
  var revealObserver = null;
  var pending = new Set();
  var passTicking = false;

  // IntersectionObserver misses elements that jump from below to above the viewport
  // in one frame (fast flicks, anchor links). Reveal anything that has been passed.
  function checkPassed() {
    passTicking = false;
    pending.forEach(function (el) {
      if (el.getBoundingClientRect().top < window.innerHeight) {
        reveal(el);
        revealObserver.unobserve(el);
      }
    });
  }

  function onScrollPassed() {
    if (passTicking || pending.size === 0) return;
    passTicking = true;
    window.requestAnimationFrame(checkPassed);
  }

  function reveal(target) {
    pending.delete(target);
    if (target.hasAttribute('data-mo-stagger')) {
      var step = staggerStep();
      var children = target.children;
      for (var i = 0; i < children.length; i++) {
        children[i].style.setProperty('--mo-delay', i * step + 'ms');
        children[i].classList.add(REVEALED);
      }
    }
    if (target.hasAttribute('data-mo-reveal')) target.classList.add(REVEALED);
  }

  function onReveal(entries) {
    entries.forEach(function (entry) {
      // Also reveal anything already scrolled past (fast flicks, anchor jumps).
      var passed = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
      if (!entry.isIntersecting && !passed) return;
      reveal(entry.target);
      revealObserver.unobserve(entry.target);
    });
  }

  function initReveal(scope) {
    if (!revealObserver) {
      revealObserver = new IntersectionObserver(onReveal, {
        rootMargin: '0px 0px -8% 0px',
        threshold: 0,
      });
    }

    var nodes = (scope || document).querySelectorAll('[data-mo-reveal], [data-mo-stagger]');
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      if (el.classList.contains(REVEALED) || el.hasAttribute('data-mo-bound')) continue;
      if (isDisabled(el)) {
        reveal(el);
        continue;
      }
      // Stagger children are handled by their parent group.
      if (el.hasAttribute('data-mo-reveal') && el.parentElement && el.parentElement.hasAttribute('data-mo-stagger')) {
        continue;
      }
      el.setAttribute('data-mo-bound', '');
      pending.add(el);
      revealObserver.observe(el);
    }
  }

  window.addEventListener('scroll', onScrollPassed, { passive: true });

  /* ---------- Scroll progress for pinned sections ---------- */
  var progressEls = [];
  var visibleProgress = new Set();
  var progressObserver = null;
  var ticking = false;

  function updateProgress() {
    ticking = false;
    var vh = window.innerHeight;
    visibleProgress.forEach(function (el) {
      var rect = el.getBoundingClientRect();
      var total = rect.height + vh;
      var value = Math.min(1, Math.max(0, (vh - rect.top) / total));
      el.style.setProperty('--mo-progress', value.toFixed(4));
    });
  }

  function requestProgress() {
    if (ticking || visibleProgress.size === 0) return;
    ticking = true;
    window.requestAnimationFrame(updateProgress);
  }

  function initProgress(scope) {
    var nodes = (scope || document).querySelectorAll('[data-mo-progress]');
    if (!nodes.length) return;

    if (!progressObserver) {
      progressObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) visibleProgress.add(entry.target);
          else visibleProgress.delete(entry.target);
        });
        requestProgress();
      });
      window.addEventListener('scroll', requestProgress, { passive: true });
      window.addEventListener('resize', requestProgress, { passive: true });
    }

    for (var i = 0; i < nodes.length; i++) {
      if (progressEls.indexOf(nodes[i]) !== -1) continue;
      if (isDisabled(nodes[i])) {
        nodes[i].style.setProperty('--mo-progress', '1');
        continue;
      }
      progressEls.push(nodes[i]);
      progressObserver.observe(nodes[i]);
    }
  }

  function forgetProgress(scope) {
    progressEls = progressEls.filter(function (el) {
      if (!scope.contains(el)) return true;
      progressObserver.unobserve(el);
      visibleProgress.delete(el);
      return false;
    });
  }

  /* ---------- Parallax, pinned progress and count up ----------
     [data-mo-parallax='0.15']  translates the element against the scroll (speed 0 to 0.5),
                                sets --mo-parallax-y on itself
     [data-mo-pin]              tall wrapper of a position: sticky stage; receives --mo-pin
                                (0 when its top reaches the viewport top, 1 when its bottom
                                reaches the viewport bottom)
     [data-mo-count]            number counts up from 0 once visible */
  var motionEls = new Set();
  var motionObserver = null;
  var motionTicking = false;

  function updateMotion() {
    motionTicking = false;
    var vh = window.innerHeight;
    motionEls.forEach(function (el) {
      var rect = el.getBoundingClientRect();
      if (el.hasAttribute('data-mo-pin')) {
        var range = Math.max(1, rect.height - vh);
        var p = Math.min(1, Math.max(0, -rect.top / range));
        el.style.setProperty('--mo-pin', p.toFixed(4));
      }
      if (el.hasAttribute('data-mo-parallax')) {
        var speed = parseFloat(el.getAttribute('data-mo-parallax')) || 0.15;
        var offset = (rect.top + rect.height / 2 - vh / 2) * -speed;
        el.style.setProperty('--mo-parallax-y', offset.toFixed(1) + 'px');
      }
    });
  }

  function requestMotion() {
    if (motionTicking || motionEls.size === 0) return;
    motionTicking = true;
    window.requestAnimationFrame(updateMotion);
  }

  function initMotion(scope) {
    var nodes = (scope || document).querySelectorAll('[data-mo-parallax], [data-mo-pin]');
    if (!nodes.length) return;
    if (!motionObserver) {
      motionObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) motionEls.add(entry.target);
            else motionEls.delete(entry.target);
          });
          requestMotion();
        },
        { rootMargin: '20% 0px 20% 0px' }
      );
      window.addEventListener('scroll', requestMotion, { passive: true });
      window.addEventListener('resize', requestMotion, { passive: true });
    }
    for (var i = 0; i < nodes.length; i++) {
      if (nodes[i].hasAttribute('data-mo-motion-bound')) continue;
      if (isDisabled(nodes[i])) {
        nodes[i].style.setProperty('--mo-pin', '1');
        continue;
      }
      nodes[i].setAttribute('data-mo-motion-bound', '');
      motionObserver.observe(nodes[i]);
    }
  }

  function forgetMotion(scope) {
    if (!motionObserver) return;
    scope.querySelectorAll('[data-mo-motion-bound]').forEach(function (el) {
      motionObserver.unobserve(el);
      motionEls.delete(el);
    });
  }

  var countObserver = null;

  function runCount(el) {
    var target = el.getAttribute('data-mo-count');
    var end = parseFloat(target);
    if (isNaN(end)) return;
    var decimals = (target.split('.')[1] || '').length;
    var start = null;
    var duration = 1400;
    function frame(ts) {
      if (start === null) start = ts;
      var t = Math.min(1, (ts - start) / duration);
      var eased = 1 - Math.pow(1 - t, 3);
      el.textContent = (end * eased).toFixed(decimals);
      if (t < 1) window.requestAnimationFrame(frame);
      else el.textContent = target;
    }
    window.requestAnimationFrame(frame);
  }

  function initCount(scope) {
    var nodes = (scope || document).querySelectorAll('[data-mo-count]');
    if (!nodes.length) return;
    if (!countObserver) {
      countObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            countObserver.unobserve(entry.target);
            runCount(entry.target);
          });
        },
        { threshold: 0.6 }
      );
    }
    for (var i = 0; i < nodes.length; i++) {
      if (isDisabled(nodes[i]) || nodes[i].hasAttribute('data-mo-count-bound')) continue;
      nodes[i].setAttribute('data-mo-count-bound', '');
      countObserver.observe(nodes[i]);
    }
  }

  /* ---------- Boot ---------- */
  function init(scope) {
    if (!enabled()) {
      revealAll(scope);
      return;
    }
    initReveal(scope);
    initProgress(scope);
    initMotion(scope);
    initCount(scope);
  }

  window.MoReveal = { init: init, revealAll: revealAll };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  // Visitor switches on reduced motion mid visit: show everything at once.
  var onMotionChange = function () {
    if (reducedMotion.matches) {
      root.classList.remove('mo-anim');
      revealAll(document);
    }
  };
  if (reducedMotion.addEventListener) reducedMotion.addEventListener('change', onMotionChange);
  else if (reducedMotion.addListener) reducedMotion.addListener(onMotionChange);

  // Theme editor: bind sections as they are added, reveal selected blocks immediately.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
  document.addEventListener('shopify:section:unload', function (event) {
    if (progressObserver) forgetProgress(event.target);
    forgetMotion(event.target);
  });
  document.addEventListener('shopify:block:select', function (event) {
    revealAll(event.target.parentElement || event.target);
  });
})();
